import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { primaryNav, secondaryNav, siteInfo } from '@/mocks/gossip';

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const today = new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  }).format(new Date());

  const isActive = (path: string) => location.pathname === path;

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setSearchOpen(false);
    setKeyword('');
  };

  return (
    <header className="relative z-50">
      <div className="bg-foreground-950 text-background-100/75">
        <div className="mx-auto flex h-9 max-w-[1240px] items-center justify-between px-4 text-[11px] tracking-wide md:px-8 md:text-xs">
          <span className="whitespace-nowrap">{today}</span>
          <div className="flex items-center gap-5">
            <span className="hidden whitespace-nowrap sm:inline">合作邮箱：{siteInfo.cooperation}</span>
            {secondaryNav.slice(0, 1).map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className="hidden whitespace-nowrap transition-colors hover:text-accent-300 md:inline"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50">
        <div className="mx-auto flex max-w-[1240px] flex-col items-center px-4 py-5 md:py-7">
          <Link to="/" className="flex flex-col items-center gap-2" aria-label="诱惑吃瓜 首页">
            <span className="relative flex items-center gap-2 font-heading text-3xl font-black tracking-[0.18em] text-foreground-950 md:text-[42px]">
              <span className="flex h-2 w-2 items-center justify-center rounded-full bg-primary-500" aria-hidden="true" />
              诱惑吃瓜
              <span className="flex h-2 w-2 items-center justify-center rounded-full bg-accent-500" aria-hidden="true" />
            </span>
            <span className="text-[10px] tracking-[0.42em] text-foreground-500 md:text-xs">
              {siteInfo.slogan}
            </span>
          </Link>
        </div>
      </div>

      <nav
        aria-label="主导航"
        className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur"
      >
        <div className="mx-auto flex h-12 max-w-[1240px] items-center justify-between px-4 md:px-8">
          <div className="hidden items-center gap-1 md:flex">
            {primaryNav.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative flex h-12 items-center whitespace-nowrap px-3 text-sm transition-colors ${
                  isActive(item.path)
                    ? 'font-semibold text-primary-600'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                {item.label}
                {isActive(item.path) && (
                  <span className="absolute inset-x-3 bottom-0 h-[3px] rounded-full bg-primary-500" />
                )}
              </Link>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-800 transition-colors hover:bg-background-100 md:hidden"
            aria-label="打开导航菜单"
            aria-expanded={mobileOpen}
          >
            <i className={mobileOpen ? 'ri-close-line' : 'ri-menu-2-line'} aria-hidden="true"></i>
          </button>

          <div className="flex items-center gap-1">
            {searchOpen ? (
              <form onSubmit={submitSearch} className="flex items-center gap-2">
                <input
                  autoFocus
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  type="search"
                  placeholder="搜索美照、红毯、同框"
                  aria-label="搜索站内内容"
                  className="h-9 w-40 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 sm:w-56"
                />
                <button
                  type="submit"
                  className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
                  aria-label="提交搜索"
                >
                  <i className="ri-search-line" aria-hidden="true"></i>
                </button>
              </form>
            ) : (
              <button
                type="button"
                onClick={() => setSearchOpen(true)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-lg text-foreground-800 transition-colors hover:bg-background-100"
                aria-label="展开搜索"
              >
                <i className="ri-search-line" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        {mobileOpen && (
          <div className="border-t border-background-200 bg-background-50 md:hidden">
            <div className="grid grid-cols-2 gap-2 px-4 py-4">
              {[...primaryNav, ...secondaryNav].map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center justify-between rounded-md border px-3 py-2.5 text-sm transition-colors ${
                    isActive(item.path)
                      ? 'border-primary-300 bg-primary-50 text-primary-700'
                      : 'border-background-200 bg-background-100 text-foreground-800'
                  }`}
                >
                  {item.label}
                  <i className="ri-arrow-right-s-line text-base" aria-hidden="true"></i>
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}